import gql from 'graphql-tag';

import { type CustomerPersona, mapPersonaForBackendInput } from 'src/data/persona';
import { executeGqlMutation } from 'src/graphql/client';
import { stringifyIfPresent } from 'src/lib/common';
import { getSingleProp } from 'src/lib/graphql';
import type { BackendJob, BackendMutationGenerateShortFormAssetsArgs } from 'src/types/backend';

type Variables = Omit<
  BackendMutationGenerateShortFormAssetsArgs,
  'async' | 'customerPersonaId' | 'customerPersona'
> & {
  customerPersona?: CustomerPersona;
};

export type FeedJob = Pick<BackendJob, 'id' | 'status'>;

interface Options {
  async?: boolean;
  abortSignal?: AbortSignal;
}

const MUTATION = gql`
  mutation GenerateShortFormAssets(
    $async: Boolean
    $sectionId: ID!
    $projectId: ID!
    $generationTool: GenerationToolEnum!
    $subGenerationToolsProps: JSON
    $brief: BriefInput!
    $inputText: String
    $scoringMethod: ScoringMethodInput
    $suggestionMode: String!
    $language: String!
    $languageTargetFormality: LanguageTargetFormalityEnum
    $numberOfVariations: Int!
    $tovInput: TovInput
    $debugAcPrompt: String
    $freestyleToolId: ID
    $freestyleToolAllInstructions: String
    $freestyleToolAllInstructionsData: JSON
    $customerPersonaId: ID
    $customerPersona: CustomerPersonaInput
    $annotations: [SuggestionAnnotationInput!]
    $sourceType: String
    $url: String
    $manualInstructions: String
    $avoidWords: [String!]
    $onlyImproveScore: Boolean
    $originalVariationId: ID
    $extractedBrief: String
    $urlExtractions: [UrlExtractionInput!]
    $callToAction: String
    $generationCustomModelId: ID
    $imageAspectRatio: String
    $imageModel: String
    $imageStyleId: ID
    $imageOverlayProperties: ImageOverlayTextInput
    $availableTools: JSON
    $structureInstructionsData: JSON
    $mediaGenerationProperties: MediaGenerationInput
    $subTemplateProperties: SubTemplateInput
  ) {
    generateShortFormAssets(
      async: $async
      sectionId: $sectionId
      projectId: $projectId
      generationTool: $generationTool
      subGenerationToolsProps: $subGenerationToolsProps
      brief: $brief
      inputText: $inputText
      scoringMethod: $scoringMethod
      suggestionMode: $suggestionMode
      language: $language
      languageTargetFormality: $languageTargetFormality
      numberOfVariations: $numberOfVariations
      tovInput: $tovInput
      debugAcPrompt: $debugAcPrompt
      freestyleToolId: $freestyleToolId
      freestyleToolAllInstructions: $freestyleToolAllInstructions
      freestyleToolAllInstructionsData: $freestyleToolAllInstructionsData
      customerPersonaId: $customerPersonaId
      customerPersona: $customerPersona
      annotations: $annotations
      sourceType: $sourceType
      url: $url
      manualInstructions: $manualInstructions
      avoidWords: $avoidWords
      onlyImproveScore: $onlyImproveScore
      originalVariationId: $originalVariationId
      extractedBrief: $extractedBrief
      urlExtractions: $urlExtractions
      callToAction: $callToAction
      generationCustomModelId: $generationCustomModelId
      imageAspectRatio: $imageAspectRatio
      imageModel: $imageModel
      imageStyleId: $imageStyleId
      imageOverlayProperties: $imageOverlayProperties
      availableTools: $availableTools
      structureInstructionsData: $structureInstructionsData
      mediaGenerationProperties: $mediaGenerationProperties
      subTemplateProperties: $subTemplateProperties
    ) {
      id
      status
    }
  }
`;

export const generateShortFormAssets = async (
  {
    subGenerationToolsProps,
    freestyleToolAllInstructionsData,
    customerPersona,
    structureInstructionsData,
    ...rest
  }: Variables,
  options: Options | null = null
): Promise<FeedJob | null> => {
  const variables = {
    ...rest,
    async: options?.async ?? false,
    subGenerationToolsProps: stringifyIfPresent(subGenerationToolsProps),
    freestyleToolAllInstructionsData: stringifyIfPresent(freestyleToolAllInstructionsData),
    customerPersonaId: customerPersona?.id,
    customerPersona: customerPersona ? mapPersonaForBackendInput(customerPersona) : null,
    structureInstructionsData: stringifyIfPresent(structureInstructionsData),
  };

  const response = await executeGqlMutation({
    mutation: MUTATION,
    variables,
    options: {
      abortSignal: options?.abortSignal,
      traceAcRequests: true,
    },
  });

  return getSingleProp<FeedJob>(response.data) ?? null;
};
